import React, { useRef } from 'react';
import { useMusicPlayer } from '../../contexts/MusicPlayerContext';
import {
  Search,
  X,
  Plus,
  Play,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle2,
} from 'lucide-react';

const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

function SongListPanel() {
  const {
    songListItems,
    songListPage,
    songListTotal,
    songListTotalPages,
    songListLoading,
    songListSearch,
    setSongListSearch,
    fetchSongList,
    toggleSongList,
    addToPlaylist,
    addAndPlay,
    playlist,
  } = useMusicPlayer();

  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // 자동 포커스 제거 — 모바일에서 키보드가 바로 뜨는 것 방지

  const handleSearch = (e) => {
    const val = e.target.value;
    setSongListSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSongList(1, val);
    }, 400);
  };

  const handlePage = (page) => {
    fetchSongList(page, songListSearch);
  };

  const isInPlaylist = (id) => playlist.some((t) => t.id === id);

  const toTrack = (song) => ({
    ...song,
    artistKr: song.album?.artist?.nameKr || '',
    albumImage: song.album?.image || FALLBACK_IMG,
    albumName: song.album?.nameKr || '',
  });

  return (
    <div className="fixed bottom-[98px] sm:bottom-[68px] right-0 z-50 w-full sm:w-96 max-h-[70vh] sm:max-h-[520px] bg-[#1a2940] border border-white/10 sm:rounded-tl-xl rounded-t-xl shadow-2xl flex flex-col overflow-hidden">
      {/* 헤더: 검색 */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 shrink-0">
        <Search size={14} className="text-slate-400 shrink-0" />
        <input
          ref={searchRef}
          type="text"
          value={songListSearch}
          onChange={handleSearch}
          placeholder="곡명, 아티스트 검색..."
          className="flex-1 bg-transparent text-sm text-white placeholder-slate-500 outline-none"
        />
        <button
          onClick={toggleSongList}
          className="text-slate-500 hover:text-white transition-colors ml-1 shrink-0">
          <X size={16} />
        </button>
      </div>

      {/* 곡 목록 */}
      <div className="flex-1 overflow-y-auto player-scroll">
        {songListLoading ? (
          <div className="flex items-center justify-center py-12 gap-2 text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin" />
            불러오는 중...
          </div>
        ) : songListItems.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-slate-500 text-sm">
            {songListSearch ? '검색 결과가 없습니다' : '재생 가능한 곡이 없습니다'}
          </div>
        ) : (
          <ul>
            {songListItems.map((song) => {
              const inList = isInPlaylist(song.id);
              return (
                <li
                  key={song.id}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 group transition-colors cursor-pointer"
                  onClick={() => addAndPlay(toTrack(song))}>
                  <img
                    src={song.album?.image || FALLBACK_IMG}
                    alt=""
                    className="w-9 h-9 rounded-md object-cover shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{song.nameKr}</p>
                    <p className="text-slate-400 text-xs truncate">
                      {song.album?.artist?.nameKr}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {/* 플레이리스트 추가 버튼 */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!inList) addToPlaylist(toTrack(song));
                      }}
                      title={inList ? '담기 완료' : '담기'}
                      className={`p-1 rounded transition-colors ${
                        inList
                          ? 'text-sky-400'
                          : 'text-slate-500 hover:text-sky-400 opacity-0 group-hover:opacity-100'
                      }`}>
                      {inList ? <CheckCircle2 size={15} /> : <Plus size={15} />}
                    </button>
                    {/* 즉시 재생 버튼 */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        addAndPlay(toTrack(song));
                      }}
                      title="바로 재생"
                      className="p-1 rounded text-slate-500 hover:text-sky-400 opacity-0 group-hover:opacity-100 transition-colors">
                      <Play size={15} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* 페이지네이션 */}
      {songListTotalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/10 shrink-0">
          <span className="text-slate-500 text-xs">{songListTotal.toLocaleString()}곡</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePage(songListPage - 1)}
              disabled={songListPage <= 1}
              className="p-1 text-slate-400 hover:text-white disabled:opacity-30 transition-colors">
              <ChevronLeft size={15} />
            </button>
            <span className="text-slate-400 text-xs px-2">
              {songListPage} / {songListTotalPages}
            </span>
            <button
              onClick={() => handlePage(songListPage + 1)}
              disabled={songListPage >= songListTotalPages}
              className="p-1 text-slate-400 hover:text-white disabled:opacity-30 transition-colors">
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SongListPanel;
